import React, { useState } from "react";
import styled from "@emotion/styled";
import {
  fontSize,
  fontWeight,
  spacing,
  borderRadius,
  border as borderToken,
  interactive,
  surface,
  text,
} from "@atlas-int/tokens";
import PreviewSection from "../components/PreviewSection";
import CodeBlock from "../components/CodeBlock";
import MobileNotification from "../../../components/modals/MobileNotification";

// ─── Styled helpers ──────────────────────────────────────────────────────────

const SubSection = styled.div`
  margin-bottom: ${spacing[12]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const SubTitle = styled.h3`
  font-size: ${fontSize.lg};
  font-weight: ${fontWeight.semibold};
  color: ${text.primary};
  margin: 0 0 ${spacing[4]} 0;
`;

const Description = styled.p`
  font-size: ${fontSize.base};
  color: ${text.secondary};
  margin: 0 0 ${spacing[6]} 0;
`;

const Note = styled.div`
  font-size: ${fontSize.sm};
  color: ${text.muted};
  padding: ${spacing[4]} ${spacing[6]};
  background-color: ${surface.muted};
  border-radius: ${borderRadius.base};
  margin-top: ${spacing[6]};
`;

const TriggerButton = styled.button`
  padding: ${spacing[2]} ${spacing[8]};
  font-size: ${fontSize.sm};
  font-weight: ${fontWeight.semibold};
  border: 1px solid ${borderToken.default};
  border-radius: ${borderRadius.base};
  cursor: pointer;
  background-color: ${interactive.default};
  color: ${text.onDark};

  &:hover {
    background-color: ${interactive.hover};
  }
`;

// ─── MobileNotification Section ─────────────────────────────────────────────

const MobileNotificationDemo: React.FC = () => {
  const [showNotification, setShowNotification] = useState(false);

  return (
    <SubSection>
      <SubTitle>MobileNotification</SubTitle>
      <Description>
        Full-screen overlay shown on mobile devices to indicate the Atlas is best
        viewed on wider screens. Includes the Growth Lab logo and a close button.
      </Description>
      <TriggerButton onClick={() => setShowNotification(true)}>
        Show Mobile Notification
      </TriggerButton>
      {showNotification && (
        <MobileNotification onClose={() => setShowNotification(false)} />
      )}
      <CodeBlock
        code={`import MobileNotification from "components/modals/MobileNotification";

const [show, setShow] = useState(true);

{show && <MobileNotification onClose={() => setShow(false)} />}`}
      />
    </SubSection>
  );
};

// ─── InfoBanner Section ─────────────────────────────────────────────────────

const InfoBannerDemo: React.FC = () => (
  <SubSection>
    <SubTitle>InfoBanner</SubTitle>
    <Description>
      Dismissible announcement banner that fetches active banners from a GraphQL API.
      Displays banner text, a CTA link, and navigation between multiple banners.
      Tracks dismissed banners in localStorage.
    </Description>
    <Note>
      InfoBanner depends on Apollo Client for the <code>GetBanner</code> GraphQL query
      and on the global CSS grid (<code>gridAreas.infoBanner</code>). Cannot render standalone.
    </Note>
    <CodeBlock
      code={`import InfoBanner from "components/InfoBanner";

// Renders in the infoBanner grid area of GlobalGrid.
// Fetches banners from GraphQL, filters by startTime/endTime,
// and tracks dismissed banners via localStorage.
<InfoBanner />

// Banner data shape:
interface Banner {
  bannerId: string;
  text: string;
  startTime: string;
  endTime: string;
  ctaText: string;
  ctaLink: string;
}`}
    />
  </SubSection>
);

// ─── SettingsPanel Section ──────────────────────────────────────────────────

const SettingsPanelDemo: React.FC = () => (
  <SubSection>
    <SubTitle>SettingsPanel</SubTitle>
    <Description>
      Data-driven settings panel using CompactSettingsDropdown with radio groups.
      Settings vary by visualization type (ColorBy, ProductLevel, TradeFlow, etc.).
    </Description>
    <Note>
      CompactSettingsDropdown receives <code>SettingsGroup[]</code> from the
      <code>useSettingsGroups()</code> hook. Cannot render standalone.
    </Note>
    <CodeBlock
      code={`import CompactSettingsDropdown from "components/controls/settingsPanel/CompactSettingsDropdown";

// Rendered conditionally when the settings gear icon is clicked.
// Uses data-driven radio groups via SettingsGroup[] from useSettingsGroups().
// Settings vary by vizType (Detail Level, Trade Flow, Color By, Layout, etc.)`}
    />
  </SubSection>
);

// ─── HamburgerMenu Section ──────────────────────────────────────────────────

const HamburgerMenuDemo: React.FC = () => (
  <SubSection>
    <SubTitle>HamburgerMenu</SubTitle>
    <Description>
      Expandable navigation menu that slides out from the sidebar. Contains all
      top-level navigation links with expandable sub-menus. Uses{" "}
      <code>headerLinkMetadata</code> for route definitions.
    </Description>
    <Note>
      HamburgerMenu depends on <code>react-router</code> Link and the{" "}
      <code>headerLinkMetadata</code> route configuration. Cannot render
      standalone outside the navigation context.
    </Note>
    <CodeBlock
      code={`import HamburgerMenu from "components/navigationBar/HamburgerMenu";

// Rendered when the hamburger icon is clicked in the sidebar nav.
// Slides open to navBarExpandedWidth (300px).
<HamburgerMenu closeMenu={() => setMenuOpen(false)} />

// Each top-level item can expand to show sub-menu links.
// Uses headerLinkMetadata for route definitions.
// Styled with token-based colors (navDark, textOnDark).`}
    />
  </SubSection>
);

// ─── Main Section ────────────────────────────────────────────────────────────

const NavigationSection: React.FC = () => (
  <>
    <PreviewSection
      id="mobile-notification"
      title="MobileNotification"
      description="Mobile device notification overlay"
    >
      <MobileNotificationDemo />
    </PreviewSection>

    <PreviewSection
      id="info-banner"
      title="InfoBanner"
      description="Dismissible announcement banner"
    >
      <InfoBannerDemo />
    </PreviewSection>

    <PreviewSection
      id="settings-panel"
      title="SettingsPanel"
      description="Visualization settings panel"
    >
      <SettingsPanelDemo />
    </PreviewSection>

    <PreviewSection
      id="hamburger-menu"
      title="HamburgerMenu"
      description="Expandable sidebar navigation menu"
    >
      <HamburgerMenuDemo />
    </PreviewSection>
  </>
);

export default NavigationSection;
